import React, { useEffect } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { Button } from '@rallyshift/design-system'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../../components/LoadingSpinner'

/**
 * Frame around every staff screen. Tenant Layout is the organisation's; this one is
 * ours, and it has to work on a host that names no organisation at all.
 */
const PlatformShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, isPlatformAdmin, signOut } = useAuth()
  const location = useLocation()

  useEffect(() => {
    document.title = 'RallyShift — platform'
  }, [])

  if (loading) return <LoadingSpinner />
  if (!user) {
    return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname)}`} replace />
  }
  if (!isPlatformAdmin) {
    return (
      <div className="min-h-screen bg-surface-page">
        <header className="flex items-center justify-center bg-action-primary px-4 py-8">
          <span className="font-display text-display-md text-white">RallyShift</span>
        </header>
        <main className="mx-auto w-full max-w-md px-4 py-8">
          <h1 className="font-display text-display-md text-text-primary">Geen toegang</h1>
          <p className="mt-2 text-md text-text-secondary">
            Dit is het RallyShift-beheer. Jouw account heeft daar geen toegang toe.
          </p>
        </main>
      </div>
    )
  }

  const on = (path: string) =>
    path === '/platform'
      ? location.pathname === '/platform'
      : location.pathname.startsWith(path)

  return (
    <div className="min-h-screen bg-surface-page">
      <header className="bg-action-primary text-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-4">
          <Link to="/platform" className="font-display text-display-sm text-white no-underline">
            RallyShift
          </Link>
          <nav className="flex items-center gap-4 text-sm font-semibold">
            <Link
              to="/platform"
              className={on('/platform') && !on('/platform/email') ? 'underline' : 'text-white/80'}
            >
              Organisaties
            </Link>
            <Link
              to="/platform/email"
              className={on('/platform/email') ? 'underline' : 'text-white/80'}
            >
              E-mail
            </Link>
          </nav>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-white/80 sm:inline">{user.email}</span>
            <Button variant="secondary" size="md" onClick={() => signOut()}>
              Uitloggen
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-8">{children}</main>
    </div>
  )
}

export default PlatformShell
